import React, { useState, useEffect, useMemo } from "react";
import ReactModal from "react-modal";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import { Alert, CircularProgress } from "@mui/material";
import { contactSchema } from "../utils/validationSchema";
import request from "../utils/request";

const MESSAGE_PLACEHOLDER = "Tell us how we can help.";

const SUPPORT_OPTIONS = [
  { value: "guest", label: "Guest" },
  { value: "host", label: "Host" },
  { value: "other", label: "Other" },
];

const SUPPORT_COPY = {
  guest: {
    badge: "Guest support",
    title: "Need help with your stay?",
    description:
      "Our support team can help with delivery issues, arrival timing, missing items, and order updates.",
    phone: "+1 (949) 414-7702",
    email: "support@bellhopt.com",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    messagePlaceholder:
      "Share your reservation name, property address, arrival date, and what support you need.",
  },
  host: {
    badge: "Host support",
    title: "Tell us about your homes and goals.",
    description:
      "Share your portfolio, PMS and arrival standards. We will follow up with a focused walkthrough.",
    emailLabel: "Work email",
    emailPlaceholder: "you@company.com",
    messagePlaceholder:
      "Share property count, PMS, locations, and the arrival experience you would like to create.",
  },
  other: {
    badge: "General support",
    title: "How can we help today?",
    description:
      "Tell us what you are trying to solve and the best way to reach you. We will route this to the right team.",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    messagePlaceholder:
      "Share context about your question and what outcome you are looking for.",
  },
};

const ContactUs = ({
  showModal,
  handleCloseModal,
  requireSupportType = false,
  initialSupportType = "",
  asPage = false,
}) => {
  const [error, setError] = useState();
  const [errorFormik, setErrorFormik] = useState();
  const [message, setMessage] = useState();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      supportType: initialSupportType,
      name: "",
      email: "",
      phone: "",
      message: "",
    },
    validationSchema: contactSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      setError(null);
      setErrorFormik(null);

      if (requireSupportType && !values.supportType) {
        setErrorFormik("Please choose Guest, Host, or Other to continue.");
        return;
      }

      if (!values.email || !values.phone || !values.message) {
        const firstErrorKey = Object.keys(formik.errors)[0];
        setErrorFormik(
          (firstErrorKey && formik.errors[firstErrorKey]) ||
            "Please fill in all required fields."
        );
        return;
      }

      if (Object.keys(formik.errors).length > 0) {
        const firstErrorKey = Object.keys(formik.errors)[0];
        setErrorFormik(formik.errors[firstErrorKey]);
        return;
      }

      try {
        setLoading(true);
        const res = await request.post(`/contact`, values);

        if (res.data?.success) {
          setMessage(
            res.data.message || "Thanks — we'll be in touch shortly."
          );
          setError(null);
          setErrorFormik(null);
          setLoading(false);
          if (!asPage && handleCloseModal) handleCloseModal();
          formik.resetForm();
        } else {
          setError("Something went wrong. Please try again.");
          setLoading(false);
        }
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            "Something went wrong. Please try again."
        );
        setLoading(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = formik;

  const activeSupportCopy = useMemo(
    () => SUPPORT_COPY[values.supportType] || SUPPORT_COPY.host,
    [values.supportType]
  );

  useEffect(() => {
    if (!showModal) return;
    if (initialSupportType) {
      setFieldValue("supportType", initialSupportType, false);
    } else if (requireSupportType) {
      setFieldValue("supportType", "", false);
    }
  }, [showModal, initialSupportType, requireSupportType, setFieldValue]);

  useEffect(() => {
    if (!error && !errorFormik && !message) return;
    const timer = setTimeout(() => {
      setError(null);
      setErrorFormik(null);
      setMessage(null);
    }, 3200);
    return () => clearTimeout(timer);
  }, [error, errorFormik, message]);

  const cardContent = (
    <div
      className="
        relative
        w-full
        mx-auto
        rounded-2xl
        px-6 py-5
        bg-white
        border border-[#EAEAEA]
        shadow-[0_24px_80px_rgba(0,0,0,0.12)]
        text-[#111111]
      "
    >
      {/* Close button */}
      {!asPage ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (handleCloseModal) handleCloseModal();
          }}
          className="
            absolute top-4 right-4
            h-8 w-8
            flex items-center justify-center
            rounded-full
            bg-[#F7F7F8]
            border border-[#EAEAEA]
            text-[14px] text-[#888888]
            hover:text-[#111111]
            hover:bg-[#EAEAEA]
            transition-all
            z-20
            cursor-pointer
          "
          aria-label="Close"
        >
          ✕
        </button>
      ) : (
        <Link
          to="/"
          className="
            absolute top-4 left-4
            inline-flex items-center gap-1
            text-[12px] font-medium text-[#888888]
            hover:text-[#111111]
            transition-colors z-20
          "
        >
          ← Back
        </Link>
      )}

      <form
        onSubmit={handleSubmit}
        className="relative z-10 flex flex-col gap-3"
      >
        {errorFormik && (
          <Alert
            severity="warning"
            sx={{
              mb: 0,
              borderRadius: "12px",
              fontSize: "11px",
              padding: "6px 10px",
              backgroundColor: "rgba(254,243,199,0.96)",
            }}
          >
            {errorFormik}
          </Alert>
        )}

        {/* Header */}
        <div className="space-y-2 text-center">
          {requireSupportType && (
            <div className="flex flex-col gap-2 items-center">
              <p className="text-[9px] tracking-[0.18em] uppercase text-[#888888] font-medium">
                I need support as
              </p>
              <div className="inline-flex items-center rounded-full border border-[#EAEAEA] bg-[#F7F7F8] p-0.5">
                {SUPPORT_OPTIONS.map((option) => {
                  const active = values.supportType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setFieldValue("supportType", option.value);
                        setErrorFormik(null);
                      }}
                      className={`
                        px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all
                        ${
                          active
                            ? "bg-[#111111] text-white shadow-sm"
                            : "text-[#5F6368] hover:text-[#111111]"
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F7F7F8] border border-[#EAEAEA]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#888888] font-medium">
              {activeSupportCopy.badge}
            </p>
          </div>
          <h1 className="text-[18px] leading-snug font-extrabold text-[#111111]">
            {activeSupportCopy.title}
          </h1>
          <p className="text-[12px] text-[#5F6368] max-w-[320px] mx-auto leading-relaxed">
            {activeSupportCopy.description}
          </p>
        </div>

        {values.supportType === "guest" && (
          <div className="rounded-xl border border-[#EAEAEA] bg-[#F7F7F8] px-3 py-2.5 text-center">
            <p className="text-[9px] tracking-[0.16em] uppercase text-[#888888] mb-1.5">
              Guest support
            </p>
            <a
              href={`mailto:${SUPPORT_COPY.guest.email}`}
              className="
                inline-flex items-center justify-center gap-1.5
                rounded-full
                px-4 py-2
                bg-[#111111]
                text-white
                text-[12px] font-semibold
                hover:bg-[#333333]
                transition-all
              "
            >
              {SUPPORT_COPY.guest.email}
            </a>
          </div>
        )}

        {/* Name */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="contact-name"
            className="text-[9px] tracking-[0.14em] uppercase text-[#888888] font-medium text-left"
          >
            Name
            {errors.name && touched.name && (
              <span className="text-[#EF4949] ml-1">*</span>
            )}
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Your full name"
            className={`
              w-full px-3.5 py-2.5
              text-[13px] text-[#111111]
              rounded-lg
              bg-[#F7F7F8]
              border
              ${
                errors.name && touched.name
                  ? "border-[#EF4949]"
                  : "border-[#EAEAEA]"
              }
              focus:outline-none
              focus:border-[#111111]
              focus:bg-white
              placeholder:text-[#888888]
              transition-all
            `}
          />
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="contact-phone"
            className="text-[9px] tracking-[0.14em] uppercase text-[#888888] font-medium text-left"
          >
            Phone
            {errors.phone && touched.phone && (
              <span className="text-[#EF4949] ml-1">*</span>
            )}
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="text"
            placeholder="+1 555 123 4567"
            value={values.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`
              w-full px-3.5 py-2.5
              text-[13px] text-[#111111]
              rounded-lg
              bg-[#F7F7F8]
              border
              ${
                errors.phone && touched.phone
                  ? "border-[#EF4949]"
                  : "border-[#EAEAEA]"
              }
              focus:outline-none
              focus:border-[#111111]
              focus:bg-white
              placeholder:text-[#888888]
              transition-all
            `}
          />
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="contact-email"
            className="text-[9px] tracking-[0.14em] uppercase text-[#888888] font-medium text-left"
          >
            {activeSupportCopy.emailLabel}
            {errors.email && touched.email && (
              <span className="text-[#EF4949] ml-1">*</span>
            )}
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={activeSupportCopy.emailPlaceholder}
            className={`
              w-full px-3.5 py-2.5
              text-[13px] text-[#111111]
              rounded-lg
              bg-[#F7F7F8]
              border
              ${
                errors.email && touched.email
                  ? "border-[#EF4949]"
                  : "border-[#EAEAEA]"
              }
              focus:outline-none
              focus:border-[#111111]
              focus:bg-white
              placeholder:text-[#888888]
              transition-all
            `}
          />
        </div>

        {/* Message */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="contact-message"
            className="text-[9px] tracking-[0.14em] uppercase text-[#888888] font-medium text-left"
          >
            How can we help?
            {errors.message && touched.message && (
              <span className="text-[#EF4949] ml-1">*</span>
            )}
          </label>
          <textarea
            id="contact-message"
            name="message"
            rows={3}
            placeholder={MESSAGE_PLACEHOLDER}
            value={values.message}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`
              w-full px-3.5 py-2.5
              text-[13px] text-[#111111]
              rounded-lg
              bg-[#F7F7F8]
              border
              ${
                errors.message && touched.message
                  ? "border-[#EF4949]"
                  : "border-[#EAEAEA]"
              }
              focus:outline-none
              focus:border-[#111111]
              focus:bg-white
              placeholder:text-[#888888]
              resize-none
              transition-all
            `}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="
            mt-1
            w-full
            inline-flex items-center justify-center gap-2
            rounded-xl
            bg-[#111111]
            text-white
            text-[13px]
            font-bold
            py-3
            hover:bg-[#333333]
            transition-all
            disabled:opacity-70
            disabled:cursor-not-allowed
          "
        >
          {loading && (
            <CircularProgress
              color="inherit"
              size={14}
              sx={{ marginRight: "4px" }}
            />
          )}
          {loading ? "Sending..." : "Submit message"}
        </button>

        <p className="mt-0.5 text-[9px] text-center text-[#888888]">
          No spam. Just a tailored follow-up from the Bellhopt team.
        </p>
      </form>
    </div>
  );

  return (
    <>
      {message && (
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            maxWidth: 420,
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999999,
            borderRadius: "999px",
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(240,253,250,0.98)",
            boxShadow: "0 14px 40px rgba(15,23,42,0.18)",
            fontSize: "13px",
          }}
        >
          {message}
        </Alert>
      )}

      {error && (
        <Alert
          severity="warning"
          sx={{
            position: "fixed",
            maxWidth: 420,
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999999,
            borderRadius: "999px",
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(255,247,237,0.98)",
            boxShadow: "0 14px 40px rgba(15,23,42,0.18)",
            fontSize: "13px",
          }}
        >
          {error}
        </Alert>
      )}

      {asPage ? (
        <section className="min-h-screen bg-white py-8 px-4 flex items-center justify-center">
          <div className="w-full max-w-[420px]">{cardContent}</div>
        </section>
      ) : (
        <ReactModal
          isOpen={showModal}
          onRequestClose={handleCloseModal}
          ariaHideApp={false}
          appElement={document.getElementById("root") || undefined}
          shouldCloseOnOverlayClick={true}
          style={{
            overlay: {
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              zIndex: 999998,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 16px",
            },
            content: {
              position: "relative",
              inset: "auto",
              border: "none",
              background: "transparent",
              padding: 0,
              overflow: "visible",
              maxWidth: "420px",
              width: "100%",
              maxHeight: "none",
              margin: "0 auto",
            },
          }}
        >
          {cardContent}
        </ReactModal>
      )}
    </>
  );
};

export default ContactUs;
